import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell,
} from "recharts";
import { Monitor, Smartphone, Tablet, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatCard, Table } from "@/admin/ui";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const RANGES = [7, 14, 30];
const TOOLTIP_STYLE = { background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" };
const DEVICE_COLORS: Record<string, string> = {
  desktop: "#00E5FF", mobile: "#7C3AED", tablet: "#FFD600", unknown: "#4B5563",
};
const DEVICE_ICONS: Record<string, any> = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

const fmtDur = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);

const LiveBadge = ({ count }: { count: number }) => (
  <div className="bg-[#121212] border border-white/10 rounded-lg px-4 py-3 flex flex-col justify-between"
    data-testid="traffic-stat-live">
    <div className="flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
      </span>
      <span className="font-mono text-2xl font-bold text-emerald-400">{count}</span>
    </div>
    <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">Live now (5 min)</p>
  </div>
);

const PageDrilldown = ({ path, days, onClose }: { path: string; days: number; onClose: () => void }) => {
  const { data } = useQuery({
    queryKey: ["admin-traffic-page", path, days],
    queryFn: async () => (await api.get("/admin/analytics/traffic/page", { params: { path, days } })).data,
  });
  const maxRef = Math.max(...((data?.referrers || []).map((r: any) => r.landings)), 1);
  const devTotal = (Object.values(data?.devices || {}) as number[]).reduce((s, v) => s + v, 0) || 1;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-2xl" data-testid="traffic-page-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm text-[#00E5FF] break-all">{path}</DialogTitle>
        </DialogHeader>
        {!data ? (
          <p className="text-gray-500 text-sm py-8 text-center">Loading page traffic…</p>
        ) : (
          <div className="space-y-5">
            <div className="flex gap-6">
              <div>
                <p className="font-mono text-2xl font-bold text-white">{data.totals.views}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Views ({days}d)</p>
              </div>
              <div>
                <p className="font-mono text-2xl font-bold text-[#00E5FF]">{data.totals.visitors}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Unique visitors</p>
              </div>
              <div className="ml-auto flex items-end gap-3">
                {Object.entries(data.devices || {}).sort((a: any, b: any) => b[1] - a[1]).map(([name, v]: any) => (
                  <span key={name} className="font-mono text-[10px] text-gray-400 capitalize">
                    {name} <span style={{ color: DEVICE_COLORS[name] || "#4B5563" }}>
                      {Math.round((v / devTotal) * 100)}%</span>
                  </span>
                ))}
              </div>
            </div>
            <div data-testid="traffic-page-trend">
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data.series}>
                  <XAxis dataKey="day" tick={{ fill: "#6B7280", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={30} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="views" stroke="#7C3AED" fill="#7C3AED22" name="Views" />
                  <Area type="monotone" dataKey="visitors" stroke="#00E5FF" fill="#00E5FF33" name="Visitors" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Landing sources for this page</p>
              <div className="space-y-2">
                {(data.referrers || []).map((r: any) => (
                  <div key={r.source} className="flex items-center gap-3">
                    <span className="text-gray-300 text-xs w-40 truncate" title={r.source}>{r.source}</span>
                    <div className="flex-1 h-1.5 bg-white/5 rounded overflow-hidden">
                      <div className="h-full bg-[#00E5FF] rounded" style={{ width: `${(r.landings / maxRef) * 100}%` }} />
                    </div>
                    <span className="font-mono text-xs text-gray-400 w-8 text-right">{r.landings}</span>
                  </div>
                ))}
                {(data.referrers || []).length === 0 && (
                  <p className="text-gray-600 text-xs">No external landings recorded on this page — visitors arrived here by browsing the site or directly</p>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Traffic() {
  const [days, setDays] = useState(7);
  const [drillPath, setDrillPath] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ["admin-traffic", days],
    queryFn: async () => (await api.get(`/admin/analytics/traffic?days=${days}`)).data,
    refetchInterval: 60000,
  });

  if (!data) return <div className="text-gray-500 py-10">Loading traffic data...</div>;
  const t = data.totals || {};
  const deviceData = Object.entries(data.devices || {})
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);
  const deviceTotal = deviceData.reduce((s, d) => s + d.value, 0) || 1;
  const maxPage = Math.max(...(data.top_pages || []).map((p: any) => p.views), 1);
  const maxRef = Math.max(...(data.referrers || []).map((r: any) => r.landings), data.direct_sessions, 1);

  return (
    <div data-testid="admin-traffic-page">
      <PageHeader title="Traffic" sub="Site-wide visitor analytics — who's here, where they came from and what they read" />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard label="Unique visitors" value={t.visitors || 0} />
        <StatCard label="Page views" value={t.views || 0} accent="#7C3AED" />
        <StatCard label="Sessions" value={t.sessions || 0} accent="#FFD600" />
        <StatCard label="Pages / session" value={t.pages_per_session || 0} accent="#10B981" />
        <StatCard label="Avg. visit" value={fmtDur(t.avg_visit_seconds || 0)} accent="#00E5FF" sub="Per session" />
        <LiveBadge count={t.live_now || 0} />
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-lg p-5 mb-6" data-testid="traffic-daily-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm">Visitors & page views</h3>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setDays(r)} data-testid={`traffic-range-${r}`}
                className={`font-mono text-[11px] px-2.5 py-1 rounded transition-colors ${
                  days === r ? "bg-[#00E5FF] text-black font-bold" : "text-gray-500 hover:text-white hover:bg-white/10"}`}>
                {r}d
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data.series}>
            <XAxis dataKey="day" tick={{ fill: "#6B7280", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={36} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Area type="monotone" dataKey="views" stroke="#7C3AED" fill="#7C3AED22" name="Page views" />
            <Area type="monotone" dataKey="visitors" stroke="#00E5FF" fill="#00E5FF33" name="Visitors" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5" data-testid="traffic-hourly-chart">
          <h3 className="font-heading font-semibold text-white text-sm mb-4">Last 24 hours</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.hourly}>
              <XAxis dataKey="hour" tick={{ fill: "#6B7280", fontSize: 9 }} interval={3} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={30} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="views" fill="#00E5FF" name="Page views" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5" data-testid="traffic-devices-chart">
          <h3 className="font-heading font-semibold text-white text-sm mb-4">Devices</h3>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width="55%" height={200}>
              <PieChart>
                <Pie data={deviceData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}
                  paddingAngle={3} stroke="none">
                  {deviceData.map((d) => (
                    <Cell key={d.name} fill={DEVICE_COLORS[d.name] || "#4B5563"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-3">
              {deviceData.map((d) => {
                const Icon = DEVICE_ICONS[d.name];
                return (
                  <div key={d.name} className="flex items-center gap-2" data-testid={`traffic-device-${d.name}`}>
                    {Icon ? <Icon className="h-3.5 w-3.5" style={{ color: DEVICE_COLORS[d.name] }} />
                      : <span className="h-2 w-2 rounded-full" style={{ background: DEVICE_COLORS[d.name] || "#4B5563" }} />}
                    <span className="text-gray-300 text-xs capitalize">{d.name}</span>
                    <span className="font-mono text-xs text-gray-500 ml-auto">
                      {Math.round((d.value / deviceTotal) * 100)}%
                    </span>
                  </div>
                );
              })}
              {deviceData.length === 0 && <p className="text-gray-600 text-xs">No device data yet</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section data-testid="traffic-top-pages">
          <h3 className="font-heading font-semibold text-white text-sm mb-3">
            Top pages <span className="font-mono text-[10px] text-gray-600 font-normal ml-1">click a row to drill down</span>
          </h3>
          <Table headers={["Page", "Views", "Visitors"]}>
            {(data.top_pages || []).map((p: any, i: number) => (
              <tr key={p.path} onClick={() => setDrillPath(p.path)} data-testid={`traffic-page-row-${i}`}
                className="cursor-pointer hover:bg-white/[0.03] transition-colors">
                <td className="px-4 py-2.5 max-w-[280px]">
                  <p className="text-white text-xs font-mono truncate">{p.path}</p>
                  <div className="h-1 bg-white/5 rounded mt-1.5 overflow-hidden">
                    <div className="h-full bg-[#00E5FF]/60 rounded" style={{ width: `${(p.views / maxPage) * 100}%` }} />
                  </div>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-[#00E5FF]">{p.views}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{p.visitors}</td>
              </tr>
            ))}
          </Table>
        </section>
        <section data-testid="traffic-referrers">
          <h3 className="font-heading font-semibold text-white text-sm mb-3">Traffic sources</h3>
          <div className="bg-[#121212] border border-white/10 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3" data-testid="traffic-source-direct">
              <span className="text-gray-300 text-xs w-40 truncate">Direct / none</span>
              <div className="flex-1 h-1.5 bg-white/5 rounded overflow-hidden">
                <div className="h-full bg-[#7C3AED] rounded"
                  style={{ width: `${(data.direct_sessions / maxRef) * 100}%` }} />
              </div>
              <span className="font-mono text-xs text-gray-400 w-10 text-right">{data.direct_sessions}</span>
            </div>
            {(data.referrers || []).map((r: any) => (
              <div key={r.source} className="flex items-center gap-3">
                <span className="text-gray-300 text-xs w-40 truncate" title={r.source}>{r.source}</span>
                <div className="flex-1 h-1.5 bg-white/5 rounded overflow-hidden">
                  <div className="h-full bg-[#00E5FF] rounded" style={{ width: `${(r.landings / maxRef) * 100}%` }} />
                </div>
                <span className="font-mono text-xs text-gray-400 w-10 text-right">{r.landings}</span>
              </div>
            ))}
            {(data.referrers || []).length === 0 && (
              <p className="text-gray-600 text-xs">No external referrers in this period yet</p>
            )}
          </div>
        </section>
      </div>

      <p className="font-mono text-[10px] text-gray-600 mt-6">
        Unique visitor, session and device breakdowns only include traffic recorded since visitor tracking
        was enabled — page view history goes further back.
      </p>
      {drillPath && <PageDrilldown path={drillPath} days={days} onClose={() => setDrillPath(null)} />}
    </div>
  );
}
